import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  Text,
  Flex,
  Icon,
  Button,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Avatar,
  IconButton,
  SimpleGrid,
  Circle,
  Skeleton,
} from "@chakra-ui/react";
import {
  Calendar,
  Users,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  Activity,
  LucideIcon,
  UserPlus,
  CalendarClock,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import { AdminLayout } from "@/components/layout/AdminLayout";
import {
  AdminDashboard,
  dashboardService,
} from "@/lib/services/dashboardService";
import { formatDateTime } from "@/util/convertTime";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfDay,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import { getStatusColor } from "@/util/getStatusColor";
import {
  CalendarSkeleton,
  ChartSkeleton,
  PopularDoctorSkeleton,
  shimmerAnimation,
  StatCardSkeleton,
  TableSkeleton,
  TopChartSkeleton,
} from "@/components/loading/DoctorDashboardSkeleton";
import { useAccessCheck } from "@/hooks/useAccess";

interface StatCard {
  label: string;
  value:
    | "total_doctors"
    | "total_patients"
    | "total_appointments"
    | "total_revenue";
  days: string;
  icon: LucideIcon;
  color: string;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

const statCards: StatCard[] = [
  {
    label: "Doctors",
    value: "total_doctors",
    days: "7 Days",
    icon: UserPlus,
    color: "#FF6B6B",
  },
  {
    label: "Patients",
    value: "total_patients",
    days: "7 Days",
    icon: Users,
    color: "#FFD93D",
  },
  {
    label: "Appointment",
    value: "total_appointments",
    days: "7 Days",
    icon: CalendarClock,
    color: "#4ECDC4",
  },
  {
    label: "Revenue",
    value: "total_revenue",
    days: "7 Days",
    icon: DollarSign,
    color: "#2ECC71",
  },
];

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload?.length) return null;

  const total = payload.reduce(
    (sum, entry) => sum + (parseInt(`${entry.value}`) || 0),
    0,
  );

  return (
    <Box
      bg="white"
      p={4}
      border="1px"
      borderColor="gray.200"
      rounded="xl"
      shadow="2xl"
    >
      <Text fontWeight="bold" mb={3} fontSize="sm">
        {label}
      </Text>
      {payload.map((entry, index) => (
        <Flex key={index} justify="space-between" gap={6} mb={1}>
          <HStack spacing={2}>
            <Box w={3} h={3} bg={entry.color} rounded="sm" />
            <Text fontSize="sm">{entry.name.toUpperCase()}:</Text>
          </HStack>
          <Text fontSize="sm" fontWeight="semibold">
            {entry.value}
          </Text>
        </Flex>
      ))}
      <Flex
        justify="space-between"
        gap={6}
        mt={3}
        pt={3}
        borderTop="1px"
        borderColor="gray.200"
      >
        <Text fontSize="sm" fontWeight="bold">
          Total:
        </Text>
        <Text fontSize="sm" fontWeight="bold">
          {total}
        </Text>
      </Flex>
    </Box>
  );
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [dashboardData, setDashboardData] = useState<AdminDashboard | null>(
    null,
  );

  const access = useAccessCheck();

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
    }),
    [access],
  );

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days: Date[] = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }, [currentMonth]);

  const appointmentDates = useMemo(() => {
    if (dashboardData?.appointments) {
      return new Set(
        dashboardData.appointments.map((apt) =>
          format(parseISO(apt.appointment_date), "yyyy-MM-dd"),
        ),
      );
    }
    return new Set<string>();
  }, [dashboardData?.appointments]);

  const filteredUpcomingAppointments = useMemo(() => {
    if (!selectedDate || !dashboardData?.appointments)
      return dashboardData?.appointments || [];
    console.log(
      "Filtering appointments for date:",
      dashboardData.appointments.filter((apt) => {
        const aptDate = parseISO(apt.appointment_date);
        return isSameDay(aptDate, selectedDate);
      }),
    );
    return dashboardData.appointments.filter((apt) => {
      const aptDate = parseISO(apt.appointment_date);
      return isSameDay(aptDate, selectedDate);
    });
  }, [dashboardData?.appointments, selectedDate]);

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
  };

  const formatRevenue = (revenue: number): string => {
    if (revenue >= 1000000) {
      return `₱${(revenue / 1000000).toFixed(1)}m`;
    } else if (revenue >= 1000) {
      return `₱${(revenue / 1000).toFixed(1)}k`;
    }
    return `₱${revenue}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await dashboardService.getWebAdminDashboard();
        setDashboardData(data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!dashboardData && !isLoading) {
    return (
      <AdminLayout>
        <Box
          minH="calc(100vh - 110px)"
          overflowX="hidden"
          display="flex"
          justifyContent="center"
          alignItems="center"
          bg="#FEFCF8"
          py={1}
          px={5}
        >
          <Text>Failed to load dashboard data</Text>
        </Box>
      </AdminLayout>
    );
  }

  const appointmentStats = dashboardData
    ? [
        {
          label: "All Appointments",
          value: dashboardData.statistics.total_appointments,
          color: "#FF6B6B",
        },
        {
          label: "Cancelled",
          value: dashboardData.statistics.cancelled,
          color: "#E74C3C",
        },
        {
          label: "Reschedule",
          value: dashboardData.statistics.rescheduled,
          color: "#9B59B6",
        },
        {
          label: "Completed",
          value: dashboardData.statistics.completed,
          color: "#3498DB",
        },
      ]
    : [];

  return (
    <AdminLayout>
      <style>{shimmerAnimation}</style>
      <Box
        minH="calc(100vh - 110px)"
        overflowX="hidden"
        display="flex"
        flexDirection="column"
        bg="#FEFCF8"
        py={1}
        px={5}
      >
        <Flex justify="space-between" align="center" mt={3} mb={6}>
          {isLoading ? (
            <Skeleton height="40px" width="200px" />
          ) : (
            <Text fontSize="2xl" fontWeight="bold">
              Quick Stats
            </Text>
          )}
          {isLoading ? (
            <Skeleton height="32px" width="180px" />
          ) : (
            <HStack spacing={3}>
              {permissions.can_create && (
                <Button
                  onClick={() => navigate("/appointments/create-appointment")}
                  leftIcon={<Calendar size={18} />}
                  bg="#DC3611"
                  _hover={{ bg: "#bf3c1fff" }}
                  color="white"
                  size={{ base: "sm", md: "sm" }}
                  fontSize={{ base: "xs", md: "sm" }}
                >
                  New Appointment
                </Button>
              )}
              {/* <Button
                variant="outline"
                size="md"
                leftIcon={<Activity size={18} />}
              >
                Schedule Availability
              </Button> */}
            </HStack>
          )}
        </Flex>

        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={4}
          mb={6}
        >
          {isLoading ? (
            <>
              {statCards.map((_, index) => (
                <StatCardSkeleton key={index} />
              ))}
            </>
          ) : (
            statCards.map((stat, index) => {
              const summaryValue = dashboardData!.summary[stat.value];
              const last7Days = dashboardData!.summary.last_7_days![stat.value];
              const displayValue =
                stat.value === "total_revenue"
                  ? formatRevenue(summaryValue as number)
                  : summaryValue;

              return (
                <Box
                  key={index}
                  bg="white"
                  rounded="lg"
                  shadow="sm"
                  p={4}
                  position="relative"
                  overflow="hidden"
                >
                  <Flex justify="space-between" mb={3}>
                    <Circle bg={stat.color} p={3}>
                      <Icon as={stat.icon} boxSize={5} color="white" />
                    </Circle>
                    <Box textAlign="right">
                      <Badge
                        colorScheme={last7Days.change >= 0 ? "green" : "red"}
                        fontSize="xs"
                        mb={1}
                        rounded="md"
                      >
                        {last7Days.change > 0 ? "+" : ""}
                        {last7Days.change.toFixed(2)}%
                      </Badge>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        in last {stat.days}
                      </Text>
                    </Box>
                  </Flex>

                  <Text color="gray.600" fontSize="sm" mb={1}>
                    {stat.label}
                  </Text>

                  <Flex justify="space-between" align="flex-end">
                    <Text fontSize="2xl" fontWeight="bold">
                      {typeof displayValue === "string" ||
                      typeof displayValue === "number"
                        ? displayValue
                        : ""}
                    </Text>
                    <Flex align="flex-end" gap={0.5} h="32px">
                      {last7Days.daily.map((value: number, i: number) => {
                        const maxValue = Math.max(...last7Days.daily, 1);
                        const normalizedHeight = (value / maxValue) * 100;
                        return (
                          <Box
                            key={i}
                            w="6px"
                            h={`${normalizedHeight}%`}
                            minH="4px"
                            bgGradient="linear(to-t, cyan.500, purple.500)"
                            roundedTop="sm"
                          />
                        );
                      })}
                    </Flex>
                  </Flex>
                </Box>
              );
            })
          )}
        </Grid>

        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={6}>
          <Flex direction="column" gap={6}>
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <Box bg="white" rounded="lg" shadow="sm" p={6}>
                <Flex justify="space-between" align="center" mb={6}>
                  <Text fontSize="lg" fontWeight="semibold">
                    Appointment Statistics
                  </Text>
                </Flex>

                <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
                  {appointmentStats.map((item, idx) => (
                    <Box
                      key={idx}
                      bg="gray.50"
                      rounded="xl"
                      p={4}
                      textAlign="center"
                      transition="all 0.2s"
                      _hover={{ bg: "gray.100" }}
                    >
                      <HStack spacing={2} justify="center" mb={2}>
                        <Circle size="8px" bg={item.color} />
                        <Text
                          fontSize="xs"
                          color="gray.600"
                          fontWeight="medium"
                        >
                          {item.label}
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                        {item.value}
                      </Text>
                    </Box>
                  ))}
                </Grid>

                <Box h="200px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dashboardData!.statistics.per_month}
                      margin={{ top: 20, right: 10, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#E2E8F0"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#718096", fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#718096", fontSize: 12 }}
                      />
                      <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "rgba(0, 0, 0, 0.03)" }}
                      />
                      <Bar
                        dataKey="completed"
                        stackId="a"
                        fill="#3498DB"
                        radius={[0, 0, 4, 4]}
                      />
                      <Bar dataKey="cancelled" stackId="a" fill="#E74C3C" />
                      <Bar
                        dataKey="rescheduled"
                        stackId="a"
                        fill="#9B59B6"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                <Flex justify="center" gap={8} mt={4}>
                  {[
                    { label: "Completed", color: "#3498DB" },
                    { label: "Cancelled", color: "#E74C3C" },
                    { label: "Rescheduled", color: "#9B59B6" },
                  ].map((item, idx) => (
                    <HStack key={idx} spacing={2}>
                      <Box w={3} h={3} bg={item.color} rounded="sm" />
                      <Text fontSize="sm" color="gray.600">
                        {item.label}
                      </Text>
                    </HStack>
                  ))}
                </Flex>
              </Box>
            )}
            {isLoading ? (
              <PopularDoctorSkeleton />
            ) : (
              <Box bg="white" rounded="lg" shadow="sm" p={6}>
                <Flex justify="space-between" align="center" mb={5}>
                  <Text fontSize="lg" fontWeight="semibold">
                    Popular Doctors
                  </Text>
                </Flex>

                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  {dashboardData?.popular_doctors.map((doctor) => (
                    <Flex
                      key={doctor.id}
                      direction="column"
                      p={4}
                      bg="gray.50"
                      rounded="lg"
                      gap={3}
                    >
                      <Flex gap={3} align="center">
                        <Avatar
                          size="sm"
                          name={doctor.name}
                          src={doctor.avatar || "assets/img/profile.jpg"}
                          bg="teal.500"
                        />
                        <Box flex={1}>
                          <Text
                            fontWeight="semibold"
                            fontSize="sm"
                            color="gray.800"
                          >
                            {doctor.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {doctor.department}
                          </Text>
                        </Box>
                      </Flex>
                      <Text fontWeight="bold" fontSize="sm" color="gray.800">
                        {doctor?.bookings || 0}{" "}
                        <Text
                          as="span"
                          fontWeight="normal"
                          fontSize="sm"
                          color="gray.500"
                        >
                          bookings
                        </Text>
                      </Text>
                    </Flex>
                  ))}
                </Grid>
              </Box>
            )}
          </Flex>

          <Flex direction="column" gap={6}>
            {isLoading ? (
              <CalendarSkeleton />
            ) : (
              <Box
                bg="white"
                rounded="lg"
                shadow="sm"
                p={5}
                display="flex"
                flexDirection="column"
                alignSelf="flex-start"
                h="100%"
                w="100%"
              >
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="semibold">
                    Appointments
                  </Text>
                </Flex>

                <Flex justify="space-between" align="center" mb={4} px={2}>
                  <IconButton
                    aria-label="Previous month"
                    icon={<ChevronLeft size={18} />}
                    size="sm"
                    variant="ghost"
                    onClick={handlePrevMonth}
                    rounded="lg"
                  />
                  <Text fontWeight="bold" fontSize="md" color="gray.700">
                    {format(currentMonth, "MMMM yyyy")}
                  </Text>
                  <IconButton
                    aria-label="Next month"
                    icon={<ChevronRight size={18} />}
                    size="sm"
                    variant="ghost"
                    onClick={handleNextMonth}
                    rounded="lg"
                  />
                </Flex>

                <SimpleGrid columns={7} spacing={1} mb={2}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day, idx) => (
                      <Text
                        key={idx}
                        fontSize="xs"
                        fontWeight="bold"
                        color="gray.600"
                        textAlign="center"
                      >
                        {day}
                      </Text>
                    ),
                  )}
                </SimpleGrid>

                <SimpleGrid columns={7} spacing={1} mb={4}>
                  {calendarDays.map((day, index) => {
                    const isSelected =
                      selectedDate && isSameDay(day, selectedDate);
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isTodayDate = isToday(day);
                    const hasAppointment = appointmentDates.has(
                      format(day, "yyyy-MM-dd"),
                    );

                    return (
                      <Flex
                        key={index}
                        align="center"
                        justify="center"
                        h="32px"
                        fontSize="sm"
                        fontWeight={isSelected ? "bold" : "normal"}
                        color={
                          !isCurrentMonth
                            ? "gray.300"
                            : isSelected
                              ? "white"
                              : "gray.700"
                        }
                        bg={
                          isSelected
                            ? "orange.500"
                            : isTodayDate
                              ? "blue.50"
                              : "transparent"
                        }
                        rounded="md"
                        cursor="pointer"
                        onClick={() => handleDateClick(day)}
                        position="relative"
                        _hover={{
                          bg: isSelected ? "orange.500" : "gray.100",
                        }}
                        border={
                          isTodayDate && !isSelected ? "1px solid" : "none"
                        }
                        borderColor="blue.400"
                      >
                        {format(day, "d")}
                        {hasAppointment && isCurrentMonth && (
                          <Circle
                            size="4px"
                            bg={isSelected ? "white" : "orange.500"}
                            position="absolute"
                            bottom="4px"
                          />
                        )}
                      </Flex>
                    );
                  })}
                </SimpleGrid>

                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    {selectedDate
                      ? format(selectedDate, "MMMM d, yyyy")
                      : "Scheduled Today"}
                  </Text>

                  <Box maxH="200px" overflowY="auto">
                    {filteredUpcomingAppointments.length > 0 ? (
                      <VStack spacing={2} align="stretch">
                        {filteredUpcomingAppointments.slice(0, 3).map((apt) => (
                          <Flex
                            key={apt.id}
                            align="center"
                            gap={3}
                            p={3}
                            bg="gray.50"
                            rounded="lg"
                            _hover={{ bg: "gray.100" }}
                            cursor="pointer"
                            onClick={() =>
                              navigate(
                                `/appointments/view-appointment/${apt.id}`,
                              )
                            }
                          >
                            <Avatar
                              size="sm"
                              name={apt.patient?.user_fullname}
                              bg="blue.400"
                            />
                            <Box flex={1}>
                              <Text
                                fontWeight="semibold"
                                fontSize="sm"
                                color="gray.800"
                              >
                                {apt.service || "Consultation"}
                              </Text>
                              <HStack
                                spacing={1}
                                fontSize="xs"
                                color="gray.500"
                              >
                                <Icon as={Clock} boxSize={3} />
                                <Text>
                                  {format(
                                    parseISO(apt.appointment_date),
                                    "EEE, dd MMM yyyy",
                                  )}
                                  ,{" "}
                                  {format(
                                    parseISO(apt.appointment_time),
                                    "hh:mm a",
                                  )}
                                </Text>
                              </HStack>
                            </Box>
                          </Flex>
                        ))}
                      </VStack>
                    ) : (
                      <Flex
                        justify="center"
                        align="center"
                        color="gray.400"
                        py={8}
                      >
                        <Text fontSize="sm">No appointments for this date</Text>
                      </Flex>
                    )}
                  </Box>
                </VStack>
                <Button
                  w="full"
                  mt="auto"
                  size="sm"
                  variant="ghost"
                  fontWeight="medium"
                  color="gray.700"
                  bg="#F5F6F8"
                  justifyItems="flex-start"
                  onClick={() => navigate("/appointments")}
                >
                  View All Appointments
                </Button>
              </Box>
            )}
          </Flex>
        </Grid>
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            xl: "repeat(3, 1fr)",
          }}
          gap={6}
          mb={6}
          alignItems="stretch"
        >
          {isLoading ? (
            <TopChartSkeleton />
          ) : (
            <Box bg="white" rounded="lg" shadow="sm" p={6} w="100%">
              <Flex justify="space-between" align="center" mb={5}>
                <Text fontSize="lg" fontWeight="semibold">
                  Available Doctors
                </Text>
                <Button
                  variant="outline"
                  colorScheme="gray.900"
                  size="xs"
                  fontWeight="400"
                  border="1px"
                  borderColor="gray.300"
                  onClick={() => navigate("/doctors")}
                >
                  View All
                </Button>
              </Flex>

              <VStack spacing={3} align="stretch">
                {dashboardData?.available_doctors.map((doctor) => (
                  <Flex
                    key={doctor.id}
                    align="center"
                    gap={3}
                    p={3}
                    bg="gray.50"
                    rounded="lg"
                  >
                    <Avatar
                      size="sm"
                      name={doctor.name}
                      src={doctor.avatar || "assets/img/profile.jpg"}
                      bg="teal.500"
                    />
                    <Box flex={1}>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="gray.800"
                      >
                        {doctor.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {doctor.department}
                      </Text>
                    </Box>
                    <Circle size="8px" bg="green.400" />
                  </Flex>
                ))}
              </VStack>
            </Box>
          )}
          {isLoading ? (
            <TopChartSkeleton />
          ) : (
            <Box bg="white" rounded="lg" shadow="sm" p={6} w="100%">
              <Flex justify="space-between" align="center" mb={5}>
                <Text fontSize="lg" fontWeight="semibold">
                  Top 5 Patients
                </Text>
                <Button
                  variant="outline"
                  colorScheme="gray.900"
                  size="xs"
                  fontWeight="400"
                  border="1px"
                  borderColor="gray.300"
                  onClick={() => navigate("/patients")}
                >
                  View All
                </Button>
              </Flex>

              <VStack spacing={3} align="stretch">
                {dashboardData?.top_patients.map((patient) => (
                  <Flex
                    key={patient.id}
                    align="center"
                    gap={3}
                    p={3}
                    bg="gray.50"
                    rounded="lg"
                  >
                    <Avatar
                      size="sm"
                      name={patient.name}
                      src={patient.avatar || "assets/img/profile.jpg"}
                      bg="purple.500"
                    />
                    <Box flex={1}>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="gray.800"
                      >
                        {patient.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {patient.email}
                      </Text>
                    </Box>
                    <Badge
                      color="#3538CD"
                      border="1px"
                      borderColor="#3538CD"
                      rounded="md"
                      px={2}
                      textTransform="smaller"
                    >
                      {patient?.appointments || 0} appt.
                    </Badge>
                  </Flex>
                ))}
              </VStack>
            </Box>
          )}
          {isLoading ? (
            <TopChartSkeleton />
          ) : (
            <Box bg="white" rounded="lg" shadow="sm" p={6} w="100%">
              <Flex justify="space-between" align="center" mb={5}>
                <Text fontSize="lg" fontWeight="semibold">
                  Top 5 Procedures
                </Text>
              </Flex>

              <VStack spacing={3} align="stretch">
                {dashboardData?.top_services.map((service) => (
                  <Flex
                    key={service.id}
                    align="center"
                    gap={3}
                    p={3}
                    bg="gray.50"
                    rounded="lg"
                  >
                    <Box flex={1}>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="gray.800"
                      >
                        {service.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Sessions count: {service.times_used}
                      </Text>
                    </Box>
                    <Badge
                      colorScheme="blue"
                      variant="outline"
                      rounded="full"
                      px={2}
                      textTransform="smaller"
                    >
                      {service.total_revenue}
                    </Badge>
                  </Flex>
                ))}
              </VStack>
            </Box>
          )}
        </Grid>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Box bg="white" rounded="lg" shadow="sm" overflow="hidden">
            <Flex
              p={6}
              borderBottom="1px"
              borderColor="gray.200"
              justify="space-between"
              align="center"
            >
              <Text fontSize="lg" fontWeight="semibold">
                All Appointments
              </Text>
              <Button
                variant="outline"
                colorScheme="gray.900"
                size="xs"
                fontWeight="400"
                border="1px"
                borderColor="gray.300"
                onClick={() => navigate("/appointments")}
              >
                View All
              </Button>
            </Flex>

            <Box overflowX="auto">
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Doctor</Th>
                    <Th>Patient</Th>
                    <Th>Date & Time</Th>
                    <Th>Service</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {dashboardData?.all_appointments.map((appointment) => (
                    <Tr
                      key={appointment.id}
                      _hover={{ bg: "gray.50" }}
                      cursor="pointer"
                      onClick={() =>
                        navigate(
                          `/appointments/view-appointment/${appointment.id}`,
                        )
                      }
                    >
                      <Td>
                        <HStack spacing={3}>
                          <Avatar
                            size="sm"
                            name={appointment.doctor?.user_fullname}
                            bg="blue.500"
                          />
                          <Box>
                            <Text
                              fontWeight="medium"
                              fontSize="sm"
                              color="gray.800"
                            >
                              {appointment.doctor?.user_fullname || "Unknown"}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Medical Specialist
                            </Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td>
                        <HStack spacing={3}>
                          <Avatar
                            size="sm"
                            name={appointment.patient?.user_fullname}
                            bg="purple.500"
                          />
                          <Box>
                            <Text
                              fontWeight="medium"
                              fontSize="sm"
                              color="gray.800"
                            >
                              {appointment.patient?.user_fullname || "Unknown"}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {appointment.patient?.user_phone_number || "N/A"}
                            </Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td fontSize="sm" color="gray.700">
                        {formatDateTime(appointment.appointment_date)}
                      </Td>
                      <Td fontSize="sm" color="gray.700">
                        {appointment.service || "Consultation"}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={getStatusColor(appointment.status)}
                          fontSize="sm"
                          px={2}
                          py={1}
                          rounded="md"
                          fontWeight="500"
                          textTransform="none"
                          variant="outline"
                        >
                          {appointment.status}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}
      </Box>
    </AdminLayout>
  );
}
